import {
  Row,
  Box,
  Spinner,
  Text,
  Column,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Alert,
  TextWithTooltip,
} from "@hightouch/htui";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import type { SharedProps } from ".";
import type { ResolvedTemplateProps } from "@hightouch/formkit";
import { Link } from "src/router";
import { ErrorBoundary } from "react-error-boundary";

const TemplateTab = ({
  content,
  isFetching,
  name,
  children,
}: {
  content: string | undefined;
  isFetching: boolean | undefined;
  name: string;
  children: (content: string) => React.ReactNode;
}) => (
  <Box
    as={TabPanel}
    overflowY="auto"
    maxHeight="40vh"
    borderBottomRadius="2xl"
    py={0}
  >
    {isFetching ? (
      <Spinner size="lg" my={4} mx="auto" />
    ) : (
      <ErrorBoundary
        fallback={
          <Alert
            margin={4}
            type="error"
            title={`Error rendering ${name}`}
            message={`Failed to render ${name} content. Please check the template format.`}
          />
        }
      >
        {content ? (
          children(content)
        ) : (
          <Alert
            margin={4}
            type="subtle"
            title={`No ${name}`}
            message={`Select a template to preview its ${name.toLowerCase()}.`}
          />
        )}
      </ErrorBoundary>
    )}
  </Box>
);

export const FKTemplate = ({
  heading = "Email Template",
  template: { isFetching, data },
  children,
}: {
  heading?: string;
  template: { isFetching?: boolean; data?: ResolvedTemplateProps };
  children: React.ReactNode[];
} & SharedProps) => (
  <Column gap={2}>
    <Column
      bg="white"
      borderRadius="2xl"
      borderWidth="hairline"
      borderStyle="solid"
      borderColor="base.border"
    >
      <Row
        px={6}
        paddingTop={6}
        paddingBottom={3}
        gap={3}
        borderTopRadius="2xl"
        justify="space-between"
        align="center"
        bg="dark"
      >
        <TextWithTooltip size="lg" fontWeight="semibold">
          {heading}
        </TextWithTooltip>
        {children}
      </Row>

      <Column borderBottomRadius="2xl" bg="white">
        <Tabs>
          <Row flex={1} minWidth={0} justify="space-between">
            <Row as={TabList} pl={6} align="center" flex={1} minWidth={0}>
              <Tab>HTML</Tab>
              <Tab>Text</Tab>
            </Row>
            {data?.editLink && (
              <Row
                align="center"
                height="auto"
                pr={6}
                borderBottomWidth="hairline"
                borderBottomStyle="solid"
                borderBottomColor="base.border"
              >
                <Link as={Link} isExternal href={data.editLink}>
                  Edit Template
                </Link>
              </Row>
            )}
          </Row>
          <TabPanels>
            <TemplateTab
              name="HTML"
              content={data?.email.html}
              isFetching={isFetching}
            >
              {(html) => parse(DOMPurify.sanitize(html))}
            </TemplateTab>
            <TemplateTab
              name="Text"
              content={data?.email.text}
              isFetching={isFetching}
            >
              {(text) => (
                <Box paddingX={4} paddingY={4}>
                  {text.split("\n").map((line, index) => (
                    <Text key={index}>
                      {line}
                      <br />
                    </Text>
                  ))}
                </Box>
              )}
            </TemplateTab>
          </TabPanels>
        </Tabs>
      </Column>
    </Column>
  </Column>
);
